import axios from 'axios';

const productService = {
  // Product Feeds
  async getProductFeeds({ summary = false } = {}) {
    try {
      const response = await axios.get('/products/feeds', {
        params: summary ? { summary: true } : {},
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching product feeds:', error);
      throw error;
    }
  },

  async addProductFeed(feed) {
    try {
      const response = await axios.post('/products/feeds', feed);
      return response.data;
    } catch (error) {
      console.error('Error adding product feed:', error);
      throw error;
    }
  },

  async updateProductFeed(feedId, updates) {
    try {
      const response = await axios.put(`/products/feeds/${feedId}`, updates);
      return response.data;
    } catch (error) {
      console.error('Error updating product feed:', error);
      throw error;
    }
  },

  async deleteProductFeed(feedId) {
    try {
      const response = await axios.delete(`/products/feeds/${feedId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting product feed:', error);
      throw error;
    }
  },

  // Products
  async getAllProducts() {
    try {
      const response = await axios.get('/products/all');
      return response.data;
    } catch (error) {
      console.error('Error fetching all products:', error);
      throw error;
    }
  },

  async searchProducts({ query, feedId, limit } = {}) {
    const response = await axios.get('/products/search', {
      params: { q: query, feed_id: feedId, limit },
    });
    return response.data;
  },

  async getProductById(productId) {
    try {
      const response = await axios.get(`/products/${productId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching product by ID:', error);
      throw error;
    }
  },

  async addProductToFeed(feedId, product) {
    try {
      const response = await axios.post(`/products/feeds/${feedId}/products`, product);
      return response.data;
    } catch (error) {
      console.error('Error adding product to feed:', error);
      throw error;
    }
  },

  async updateProductInFeed(feedId, productId, updates) {
    try {
      const response = await axios.put(`/products/${productId}`, updates);
      return response.data;
    } catch (error) {
      console.error('Error updating product:', error);
      throw error;
    }
  },

  async moveProductToFeed(productId, targetFeedId) {
    try {
      const response = await axios.put(`/products/${productId}`, { feedId: targetFeedId });
      return response.data;
    } catch (error) {
      console.error('Error moving product to feed:', error);
      throw error;
    }
  },

  async deleteProduct(productId) {
    try {
      const response = await axios.delete(`/products/${productId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting product:', error);
      throw error;
    }
  },

  async deleteProductFromFeed(_feedId, productId) {
    return this.deleteProduct(productId);
  },

  async duplicateProduct(productId) {
    try {
      const response = await axios.post(`/products/${productId}/duplicate`);
      return response.data;
    } catch (error) {
      console.error('Error duplicating product:', error);
      throw error;
    }
  },

  async bulkUpdateProducts(payload) {
    try {
      const response = await axios.post('/products/bulk-update', payload);
      return response.data;
    } catch (error) {
      console.error('Error bulk updating products:', error);
      throw error;
    }
  },

  async importProducts(feedId, payload) {
    try {
      const response = await axios.post(`/products/feeds/${feedId}/import`, payload);
      return response.data;
    } catch (error) {
      console.error('Error importing products:', error);
      throw error;
    }
  },

  // Categories
  async getCategories() {
    try {
      const response = await axios.get('/products/categories');
      return response.data;
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  },

  async createCategory(category) {
    try {
      const response = await axios.post('/products/categories', category);
      return response.data;
    } catch (error) {
      console.error('Error creating category:', error);
      throw error;
    }
  },

  async updateCategory(categoryId, updates) {
    try {
      const response = await axios.patch(`/products/categories/${categoryId}`, updates);
      return response.data;
    } catch (error) {
      // Backward compatibility for backends still using PUT.
      if (error?.response?.status === 405 || error?.response?.status === 404) {
        const fallbackResponse = await axios.put(`/products/categories/${categoryId}`, updates);
        return fallbackResponse.data;
      }
      console.error('Error updating category:', error);
      throw error;
    }
  },

  async deleteCategory(categoryId) {
    try {
      const response = await axios.delete(`/products/categories/${categoryId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting category:', error);
      throw error;
    }
  },

  // File Upload
  async uploadFile(file, uploadConfig) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', uploadConfig.type);
      formData.append('context', uploadConfig.context);

      if (uploadConfig.productId) {
        formData.append('productId', uploadConfig.productId);
      }
      if (uploadConfig.choiceId) {
        formData.append('choiceId', uploadConfig.choiceId);
      }

      const response = await axios.post('/uploads', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  },
};

// Export individual functions for backwards compatibility
export const getProductFeeds = () => productService.getProductFeeds();
// Feeds with product cards only: no options, upsells, usps or attribute values.
export const getProductFeedSummaries = () => productService.getProductFeeds({ summary: true });
export const addProductFeed = (feed) => productService.addProductFeed(feed);
export const updateProductFeed = (feedId, updates) =>
  productService.updateProductFeed(feedId, updates);
export const deleteProductFeed = (feedId) => productService.deleteProductFeed(feedId);
export const getAllProducts = () => productService.getAllProducts();
export const getProductById = (productId) => productService.getProductById(productId);
export const addProductToFeed = (feedId, product) =>
  productService.addProductToFeed(feedId, product);
export const updateProductInFeed = (feedId, productId, updates) =>
  productService.updateProductInFeed(feedId, productId, updates);
export const moveProductToFeed = (productId, targetFeedId) =>
  productService.moveProductToFeed(productId, targetFeedId);
export const deleteProduct = (productId) => productService.deleteProduct(productId);
export const deleteProductFromFeed = (feedId, productId) =>
  productService.deleteProductFromFeed(feedId, productId);
export const duplicateProduct = (productId) => productService.duplicateProduct(productId);
export const bulkUpdateProducts = (payload) => productService.bulkUpdateProducts(payload);
export const importProducts = (feedId, payload) => productService.importProducts(feedId, payload);
export const getCategories = () => productService.getCategories();
export const createCategory = (category) => productService.createCategory(category);
export const updateCategory = (categoryId, updates) =>
  productService.updateCategory(categoryId, updates);
export const deleteCategory = (categoryId) => productService.deleteCategory(categoryId);

// Legacy function
export const getProductFeedById = async (feedId) => {
  const feeds = await getProductFeeds();
  return feeds.find((feed) => feed.id === feedId);
};

// Default export for easier importing
export default productService;
